import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Plus, 
    Edit2, 
    Trash2, 
    Eye,
    EyeOff,
    Newspaper,
    Image,
    GripVertical,
    X,
    Save,
    FileText,
    Upload,
    Loader2,
    Mail,
    Send,
    Users,
    Tag,
    BookUser,
    UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterAdminPage = () => {
    const [newsletters, setNewsletters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingNewsletter, setEditingNewsletter] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, month: '' });
    const [uploading, setUploading] = useState({ cover: false, founder: false, section: null });
    
    // Email distribution state
    const [emailModal, setEmailModal] = useState({ open: false, newsletter: null });
    const [contacts, setContacts] = useState([]);
    const [contactTags, setContactTags] = useState([]);
    const [members, setMembers] = useState([]);
    const [contactStats, setContactStats] = useState(null);
    const [emailForm, setEmailForm] = useState({
        recipientSource: 'all', // all, contacts, members
        selectedTag: '',
        includeMembers: true
    });
    const [sendingEmail, setSendingEmail] = useState(false);
    
    const coverInputRef = useRef(null);
    const founderInputRef = useRef(null);
    const sectionInputRefs = useRef({});
    
    const [formData, setFormData] = useState({
        month: '',
        volume: '',
        title: 'R&R News',
        tagline: 'Each one. Reach one. Teach one.',
        cover_image: '',
        founder_image: '',
        founders_desk_title: "From the Founder's Desk",
        founders_desk_content: '',
        sections: []
    });

    useEffect(() => {
        fetchNewsletters();
        fetchContactsData();
    }, []);

    const fetchNewsletters = async () => {
        try {
            const response = await axios.get(`${API}/newsletters`);
            setNewsletters(response.data);
        } catch (error) {
            toast.error('Failed to load newsletters');
        } finally {
            setLoading(false);
        }
    };

    const fetchContactsData = async () => {
        try {
            const [contactsRes, tagsRes, membersRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/contacts`),
                axios.get(`${API}/admin/contacts/tags`),
                axios.get(`${API}/members`),
                axios.get(`${API}/admin/contacts/stats`)
            ]);
            setContacts(contactsRes.data);
            setContactTags(tagsRes.data);
            setMembers(membersRes.data.filter(m => m.email));
            setContactStats(statsRes.data);
        } catch (error) {
            console.error('Failed to fetch contacts data:', error);
        }
    };

    const openEmailModal = (newsletter) => {
        setEmailModal({ open: true, newsletter });
        setEmailForm({
            recipientSource: 'all',
            selectedTag: '',
            includeMembers: true
        });
    };

    const getEmailRecipientCount = () => {
        if (emailForm.recipientSource === 'all') {
            return (contactStats?.total_contacts || 0) + members.length;
        } else if (emailForm.recipientSource === 'contacts') {
            const filteredContacts = emailForm.selectedTag 
                ? contacts.filter(c => c.tags?.includes(emailForm.selectedTag))
                : contacts;
            return filteredContacts.length + (emailForm.includeMembers ? members.length : 0);
        } else if (emailForm.recipientSource === 'members') {
            return members.length;
        }
        return 0;
    };

    const handleSendNewsletter = async () => {
        if (!emailModal.newsletter) return;
        
        const recipientCount = getEmailRecipientCount();
        if (recipientCount === 0) {
            toast.error('No recipients selected');
            return;
        }

        setSendingEmail(true);
        try {
            const response = await axios.post(`${API}/admin/newsletters/${emailModal.newsletter.id}/send-email`, {
                recipient_source: emailForm.recipientSource,
                selected_tag: emailForm.selectedTag || null,
                include_members: emailForm.includeMembers
            });

            if (response.data.success) {
                toast.success(`Newsletter sent to ${response.data.emails_sent} recipients!`);
                setEmailModal({ open: false, newsletter: null });
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send newsletter');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Prepare sections with order
            const sectionsWithOrder = formData.sections.map((s, idx) => ({
                ...s,
                order: idx
            }));

            const payload = {
                ...formData,
                sections: sectionsWithOrder
            };

            if (editingNewsletter) {
                await axios.put(`${API}/newsletters/${editingNewsletter.id}`, payload);
                toast.success('Newsletter updated successfully');
            } else {
                await axios.post(`${API}/newsletters`, payload);
                toast.success('Newsletter created successfully');
            }
            setIsModalOpen(false);
            resetForm();
            fetchNewsletters();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save newsletter');
        }
    };

    const handleEdit = (newsletter) => {
        setEditingNewsletter(newsletter);
        setFormData({
            month: newsletter.month,
            volume: newsletter.volume,
            title: newsletter.title || 'R&R News',
            tagline: newsletter.tagline || 'Each one. Reach one. Teach one.',
            cover_image: newsletter.cover_image || '',
            founder_image: newsletter.founder_image || '',
            founders_desk_title: newsletter.founders_desk_title || "From the Founder's Desk",
            founders_desk_content: newsletter.founders_desk_content || '',
            sections: newsletter.sections || []
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/newsletters/${id}`);
            toast.success('Newsletter deleted successfully');
            setDeleteConfirm({ open: false, id: null, month: '' });
            fetchNewsletters();
        } catch (error) {
            toast.error('Failed to delete newsletter');
        }
    };

    const handlePublishToggle = async (newsletter) => {
        try {
            if (newsletter.is_published) {
                await axios.post(`${API}/newsletters/${newsletter.id}/unpublish`);
                toast.success('Newsletter unpublished');
            } else {
                await axios.post(`${API}/newsletters/${newsletter.id}/publish`);
                toast.success('Newsletter published');
            }
            fetchNewsletters();
        } catch (error) {
            toast.error('Failed to update publish status');
        }
    };

    const resetForm = () => {
        setEditingNewsletter(null);
        setFormData({
            month: '',
            volume: '',
            title: 'R&R News',
            tagline: 'Each one. Reach one. Teach one.',
            cover_image: '',
            founder_image: '',
            founders_desk_title: "From the Founder's Desk",
            founders_desk_content: '',
            sections: []
        });
    };

    const addSection = () => {
        setFormData({
            ...formData,
            sections: [
                ...formData.sections,
                { title: '', subtitle: '', content: '', image_url: '' }
            ]
        });
    };

    const handleImageUpload = async (file, type, sectionIndex = null) => {
        if (!file) return;

        // Set uploading state
        if (type === 'cover') {
            setUploading(prev => ({ ...prev, cover: true }));
        } else if (type === 'founder') {
            setUploading(prev => ({ ...prev, founder: true }));
        } else if (type === 'section') {
            setUploading(prev => ({ ...prev, section: sectionIndex }));
        }

        try {
            // Get Cloudinary signature
            const sigResponse = await axios.get(`${API}/cloudinary/signature?folder=newsletters`);
            const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

            // Upload directly to Cloudinary
            const cloudinaryFormData = new FormData();
            cloudinaryFormData.append('file', file);
            cloudinaryFormData.append('api_key', api_key);
            cloudinaryFormData.append('timestamp', timestamp);
            cloudinaryFormData.append('signature', signature);
            cloudinaryFormData.append('folder', folder);

            const cloudinaryResponse = await fetch(
                `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
                { method: 'POST', body: cloudinaryFormData }
            );

            const cloudinaryData = await cloudinaryResponse.json();

            if (cloudinaryData.error) {
                throw new Error(cloudinaryData.error.message);
            }

            const imageUrl = cloudinaryData.secure_url;

            if (type === 'cover') {
                setFormData(prev => ({ ...prev, cover_image: imageUrl }));
            } else if (type === 'founder') {
                setFormData(prev => ({ ...prev, founder_image: imageUrl }));
            } else if (type === 'section' && sectionIndex !== null) {
                updateSection(sectionIndex, 'image_url', imageUrl);
            }

            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload image');
        } finally {
            setUploading({ cover: false, founder: false, section: null });
        }
    };

    const updateSection = (index, field, value) => {
        const updatedSections = [...formData.sections];
        updatedSections[index] = { ...updatedSections[index], [field]: value };
        setFormData({ ...formData, sections: updatedSections });
    };

    const removeSection = (index) => {
        const updatedSections = formData.sections.filter((_, i) => i !== index);
        setFormData({ ...formData, sections: updatedSections });
    };

    const moveSection = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= formData.sections.length) return;
        
        const updatedSections = [...formData.sections];
        [updatedSections[index], updatedSections[newIndex]] = [updatedSections[newIndex], updatedSections[index]];
        setFormData({ ...formData, sections: updatedSections });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-[#A0A0A0] animate-pulse">Loading newsletters...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white font-['Playfair_Display']">
                        Newsletter Management
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">Create and manage monthly newsletters</p>
                </div>
                <Button
                    onClick={() => {
                        resetForm();
                        setIsModalOpen(true);
                    }}
                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                    data-testid="create-newsletter-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Newsletter
                </Button>
            </div>

            {/* Newsletter Grid */}
            {newsletters.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {newsletters.map((newsletter) => (
                        <Card 
                            key={newsletter.id}
                            className="border-[#5A6350] overflow-hidden"
                            data-testid={`newsletter-card-${newsletter.id}`}
                        >
                            {/* Cover Image or Placeholder */}
                            <div className="h-40 bg-[#3D4435] relative">
                                {newsletter.cover_image ? (
                                    <img 
                                        src={newsletter.cover_image} 
                                        alt={newsletter.month}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Newspaper className="w-16 h-16 text-[#5A6350]" />
                                    </div>
                                )}
                                <Badge 
                                    className={`absolute top-3 right-3 ${
                                        newsletter.is_published 
                                            ? 'bg-green-600 text-white' 
                                            : 'bg-[#5A6350] text-[#A0A0A0]'
                                    }`}
                                >
                                    {newsletter.is_published ? 'Published' : 'Draft'}
                                </Badge>
                            </div>

                            <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <h3 className="font-semibold text-[#3D4435] text-lg">
                                            {newsletter.month}
                                        </h3>
                                        <p className="text-sm text-[#A0A0A0]">
                                            {newsletter.volume}
                                        </p>
                                    </div>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => openEmailModal(newsletter)}
                                            className="p-2 hover:bg-[#C9A227]/20 rounded-lg transition-colors"
                                            title="Send Newsletter via Email"
                                            data-testid={`email-newsletter-${newsletter.id}`}
                                        >
                                            <Mail className="w-4 h-4 text-[#C9A227]" />
                                        </button>
                                        <button
                                            onClick={() => handlePublishToggle(newsletter)}
                                            className="p-2 hover:bg-[#3D4435]/10 rounded-lg transition-colors"
                                            title={newsletter.is_published ? 'Unpublish' : 'Publish'}
                                            data-testid={`toggle-publish-${newsletter.id}`}
                                        >
                                            {newsletter.is_published ? (
                                                <EyeOff className="w-4 h-4 text-[#A0A0A0]" />
                                            ) : (
                                                <Eye className="w-4 h-4 text-green-600" />
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleEdit(newsletter)}
                                            className="p-2 hover:bg-[#3D4435]/10 rounded-lg transition-colors"
                                            data-testid={`edit-newsletter-${newsletter.id}`}
                                        >
                                            <Edit2 className="w-4 h-4 text-[#A0A0A0]" />
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm({ open: true, id: newsletter.id, month: newsletter.month })}
                                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                            data-testid={`delete-newsletter-${newsletter.id}`}
                                        >
                                            <Trash2 className="w-4 h-4 text-[#A64444]" />
                                        </button>
                                    </div>
                                </div>

                                <p className="text-sm text-[#666] line-clamp-2 mb-3">
                                    {newsletter.founders_desk_content?.substring(0, 100)}...
                                </p>

                                <div className="flex items-center gap-2 text-xs text-[#A0A0A0]">
                                    <FileText className="w-3 h-3" />
                                    <span>{newsletter.sections?.length || 0} sections</span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card className="border-[#5A6350]">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Newspaper className="w-16 h-16 text-[#5A6350] mb-4" />
                        <h3 className="text-xl font-semibold text-[#3D4435] mb-2">No Newsletters Yet</h3>
                        <p className="text-[#A0A0A0] mb-6 text-center">
                            Create your first newsletter to share with the community
                        </p>
                        <Button
                            onClick={() => {
                                resetForm();
                                setIsModalOpen(true);
                            }}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Newsletter
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit Newsletter Modal */}
            <Dialog open={isModalOpen} onOpenChange={(open) => {
                if (!open) {
                    setIsModalOpen(false);
                    resetForm();
                }
            }}>
                <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-[#3D4435] font-['Playfair_Display'] text-2xl">
                            {editingNewsletter ? 'Edit Newsletter' : 'Create Newsletter'}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Month & Year *</Label>
                                <Input
                                    placeholder="e.g., April 2026"
                                    value={formData.month}
                                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                                    required
                                    data-testid="newsletter-month-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Volume & Issue *</Label>
                                <Input
                                    placeholder="e.g., Volume 1, Issue 13"
                                    value={formData.volume}
                                    onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                                    required
                                    data-testid="newsletter-volume-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Newsletter Title</Label>
                                <Input
                                    placeholder="R&R News"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    data-testid="newsletter-title-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Tagline</Label>
                                <Input
                                    placeholder="Each one. Reach one. Teach one."
                                    value={formData.tagline}
                                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                                    data-testid="newsletter-tagline-input"
                                />
                            </div>
                        </div>

                        {/* Images */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Image className="w-4 h-4" />
                                    Cover Image
                                </Label>
                                <input
                                    type="file"
                                    ref={coverInputRef}
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(e.target.files[0], 'cover')}
                                />
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => coverInputRef.current?.click()}
                                        disabled={uploading.cover}
                                        className="border-[#5A6350] text-[#3D4435] hover:bg-[#F5F5F0]"
                                        data-testid="newsletter-cover-image-upload"
                                    >
                                        {uploading.cover ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Upload className="w-4 h-4 mr-2" />
                                        )}
                                        {uploading.cover ? 'Uploading...' : 'Upload Cover'}
                                    </Button>
                                    {formData.cover_image && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setFormData({ ...formData, cover_image: '' })}
                                            className="text-[#A64444]"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                                {formData.cover_image && (
                                    <img src={formData.cover_image} alt="Cover preview" className="w-full h-32 object-cover rounded-lg mt-2" />
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Image className="w-4 h-4" />
                                    Founder Image
                                </Label>
                                <input
                                    type="file"
                                    ref={founderInputRef}
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(e.target.files[0], 'founder')}
                                />
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => founderInputRef.current?.click()}
                                        disabled={uploading.founder}
                                        className="border-[#5A6350] text-[#3D4435] hover:bg-[#F5F5F0]"
                                        data-testid="newsletter-founder-image-upload"
                                    >
                                        {uploading.founder ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Upload className="w-4 h-4 mr-2" />
                                        )}
                                        {uploading.founder ? 'Uploading...' : 'Upload Founder'}
                                    </Button>
                                    {formData.founder_image && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setFormData({ ...formData, founder_image: '' })}
                                            className="text-[#A64444]"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                                {formData.founder_image && (
                                    <img src={formData.founder_image} alt="Founder preview" className="w-24 h-24 object-cover rounded-lg mt-2" />
                                )}
                            </div>
                        </div>

                        {/* Founder's Desk */}
                        <div className="space-y-4 p-4 bg-[#F5F5F0] rounded-lg">
                            <h3 className="font-semibold text-[#3D4435]">From the Founder's Desk</h3>
                            <div className="space-y-2">
                                <Label>Section Title</Label>
                                <Input
                                    value={formData.founders_desk_title}
                                    onChange={(e) => setFormData({ ...formData, founders_desk_title: e.target.value })}
                                    data-testid="newsletter-founders-title-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Content *</Label>
                                <Textarea
                                    placeholder="Write the founder's message..."
                                    value={formData.founders_desk_content}
                                    onChange={(e) => setFormData({ ...formData, founders_desk_content: e.target.value })}
                                    required
                                    rows={6}
                                    className="resize-none"
                                    data-testid="newsletter-founders-content-input"
                                />
                            </div>
                        </div>

                        {/* Sections */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-[#3D4435]">Newsletter Sections</h3>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addSection}
                                    className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white"
                                    data-testid="add-section-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Section
                                </Button>
                            </div>

                            {formData.sections.map((section, index) => (
                                <div 
                                    key={index} 
                                    className="p-4 border border-[#E0E0E0] rounded-lg space-y-4 bg-white"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <GripVertical className="w-4 h-4 text-[#A0A0A0]" />
                                            <span className="font-medium text-[#3D4435]">Section {index + 1}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => moveSection(index, -1)}
                                                disabled={index === 0}
                                                className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                                            >
                                                ↑
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveSection(index, 1)}
                                                disabled={index === formData.sections.length - 1}
                                                className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                                            >
                                                ↓
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeSection(index)}
                                                className="p-1 hover:bg-red-50 rounded text-[#A64444]"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Section Title *</Label>
                                            <Input
                                                placeholder="e.g., R&R Gathering Recap"
                                                value={section.title}
                                                onChange={(e) => updateSection(index, 'title', e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Subtitle (optional)</Label>
                                            <Input
                                                placeholder="e.g., Meet Jayrick Hayes"
                                                value={section.subtitle || ''}
                                                onChange={(e) => updateSection(index, 'subtitle', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Section Image (optional)</Label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={el => sectionInputRefs.current[index] = el}
                                            onChange={(e) => handleImageUpload(e.target.files[0], 'section', index)}
                                        />
                                        <div className="flex items-center gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => sectionInputRefs.current[index]?.click()}
                                                disabled={uploading.section === index}
                                                className="border-[#5A6350] text-[#3D4435] hover:bg-[#F5F5F0]"
                                            >
                                                {uploading.section === index ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Upload className="w-4 h-4 mr-2" />
                                                )}
                                                {uploading.section === index ? 'Uploading...' : 'Upload Image'}
                                            </Button>
                                            {section.image_url && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => updateSection(index, 'image_url', '')}
                                                    className="text-[#A64444]"
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                        {section.image_url && (
                                            <img src={section.image_url} alt="Section preview" className="w-full max-w-xs h-32 object-cover rounded-lg mt-2" />
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Content *</Label>
                                        <Textarea
                                            placeholder="Write the section content..."
                                            value={section.content}
                                            onChange={(e) => updateSection(index, 'content', e.target.value)}
                                            required
                                            rows={4}
                                            className="resize-none"
                                        />
                                    </div>
                                </div>
                            ))}

                            {formData.sections.length === 0 && (
                                <p className="text-[#A0A0A0] text-center py-4">
                                    No sections added yet. Click "Add Section" to add content sections.
                                </p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsModalOpen(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit"
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                data-testid="save-newsletter-btn"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {editingNewsletter ? 'Update Newsletter' : 'Create Newsletter'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, id: null, month: '' })}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white font-['Playfair_Display']">
                            Delete Newsletter
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-[#A0A0A0]">
                            Are you sure you want to delete the <span className="text-white font-semibold">{deleteConfirm.month}</span> newsletter? 
                            This action cannot be undone.
                        </p>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirm({ open: false, id: null, month: '' })}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleDelete(deleteConfirm.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="confirm-delete-newsletter-btn"
                        >
                            Delete Newsletter
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Email Distribution Modal */}
            <Dialog open={emailModal.open} onOpenChange={(open) => !open && setEmailModal({ open: false, newsletter: null })}>
                <DialogContent className="bg-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#3D4435]">
                            <Mail className="w-5 h-5 text-[#C9A227]" />
                            Send Newsletter via Email
                        </DialogTitle>
                    </DialogHeader>
                    
                    {emailModal.newsletter && (
                        <div className="space-y-4">
                            {/* Newsletter Preview */}
                            <div className="p-4 bg-gray-50 rounded-lg border">
                                <h4 className="font-semibold text-[#3D4435]">{emailModal.newsletter.month}</h4>
                                <p className="text-sm text-gray-500">{emailModal.newsletter.volume}</p>
                                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                    {emailModal.newsletter.founders_desk_content?.substring(0, 100)}...
                                </p>
                            </div>

                            {/* Recipient Source Selection */}
                            <div className="space-y-3">
                                <Label>Select Recipients</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        onClick={() => setEmailForm({ ...emailForm, recipientSource: 'all' })}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            emailForm.recipientSource === 'all'
                                                ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                : 'border-gray-200 hover:border-[#C9A227]/50'
                                        }`}
                                    >
                                        <Users className="w-5 h-5 mx-auto mb-1 text-[#C9A227]" />
                                        <span className="text-sm font-medium">All</span>
                                        <Badge className="mt-1 text-xs">{contacts.length + members.length}</Badge>
                                    </button>
                                    <button
                                        onClick={() => setEmailForm({ ...emailForm, recipientSource: 'contacts' })}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            emailForm.recipientSource === 'contacts'
                                                ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                : 'border-gray-200 hover:border-[#C9A227]/50'
                                        }`}
                                    >
                                        <BookUser className="w-5 h-5 mx-auto mb-1 text-[#C9A227]" />
                                        <span className="text-sm font-medium">Contacts</span>
                                        <Badge className="mt-1 text-xs">{contacts.length}</Badge>
                                    </button>
                                    <button
                                        onClick={() => setEmailForm({ ...emailForm, recipientSource: 'members' })}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            emailForm.recipientSource === 'members'
                                                ? 'border-[#C9A227] bg-[#C9A227]/10'
                                                : 'border-gray-200 hover:border-[#C9A227]/50'
                                        }`}
                                    >
                                        <UserCheck className="w-5 h-5 mx-auto mb-1 text-[#C9A227]" />
                                        <span className="text-sm font-medium">Members</span>
                                        <Badge className="mt-1 text-xs">{members.length}</Badge>
                                    </button>
                                </div>
                            </div>

                            {/* Tag Filter (for contacts) */}
                            {emailForm.recipientSource === 'contacts' && contactTags.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Tag className="w-4 h-4" />
                                        Filter by Tag
                                    </Label>
                                    <div className="flex gap-2 flex-wrap">
                                        <Button
                                            variant={emailForm.selectedTag === '' ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setEmailForm({ ...emailForm, selectedTag: '' })}
                                            className={emailForm.selectedTag === '' ? 'bg-[#3D4435]' : ''}
                                        >
                                            All Contacts
                                        </Button>
                                        {contactTags.map((t) => (
                                            <Button
                                                key={t.tag}
                                                variant={emailForm.selectedTag === t.tag ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setEmailForm({ ...emailForm, selectedTag: t.tag })}
                                                className={emailForm.selectedTag === t.tag ? 'bg-[#C9A227]' : ''}
                                            >
                                                {t.tag} ({t.count})
                                            </Button>
                                        ))}
                                    </div>
                                    
                                    {/* Include Members Checkbox */}
                                    <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 mt-3">
                                        <input
                                            type="checkbox"
                                            checked={emailForm.includeMembers}
                                            onChange={(e) => setEmailForm({ ...emailForm, includeMembers: e.target.checked })}
                                            className="w-4 h-4 text-[#C9A227] rounded"
                                        />
                                        <span className="text-sm">Also include all registered members ({members.length})</span>
                                    </label>
                                </div>
                            )}

                            {/* Recipient Summary */}
                            <div className="p-4 bg-[#3D4435]/5 rounded-lg border border-[#3D4435]/20">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-5 h-5 text-[#C9A227]" />
                                        <span className="font-medium text-[#3D4435]">
                                            {getEmailRecipientCount()} Recipients
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setEmailModal({ open: false, newsletter: null })}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSendNewsletter}
                                    disabled={sendingEmail || getEmailRecipientCount() === 0}
                                    className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                    data-testid="send-newsletter-email-btn"
                                >
                                    {sendingEmail ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4 mr-2" />
                                            Send to {getEmailRecipientCount()} Recipients
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NewsletterAdminPage;
